import {type ReactNode, useState} from 'react';
import {DollarSign, Film, Globe, Maximize2} from 'lucide-react';
import {Badge, Card, CardContent, CardFooter} from '@kroll-resolver-crisp/ui-components-react';
import {type VideoStatus} from '@/api/videos';
import {
  BoolValue,
  DescriptionDisclosure,
  DetailField,
  MetaGrid,
  MoreDetails,
  PlatformStatus,
  PriorSubmissions,
  TagList,
  formatDate
} from '@/components/channels/StatusCardParts';
import {formatCompactNumber} from '@/lib/itemMetadata';
import {TranslateToggle} from '@/components/shared/TranslateToggle';
import {ImageLightbox} from '@/components/shared/ImageLightbox';
import {useTranslatedText} from '@/store/translationStore';
import {cn} from '@/lib/utils';
import {
  BLUR_IMAGE_CLASS,
  SENSITIVE_CONTROLS_VISIBILITY,
  SensitiveMediaControls,
  useSensitiveMedia
} from '@/components/shared/SensitiveMedia';
import {Thumbnail} from '@/components/shared/Thumbnail';

// YouTube's contentDetails.duration is ISO 8601 (e.g. "PT4M12S", or "P1DT2H3M" for a day-plus
// video); render as "4:12"/"1:04:12"/"26:03:00". Returns null for anything else (e.g. a
// livestream's "P0D", which has no meaningful duration to show).
const ISO_DURATION_PATTERN = /^P(?:(\d+)D)?T(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?$/;

export function formatIsoDuration(iso: string | null): string | null {
  const match = iso ? ISO_DURATION_PATTERN.exec(iso) : null;
  if (!match || match.slice(1).every((g) => g === undefined)) {
    return null;
  }

  const [, d, h, m, s] = match;
  const hours = Number(d ?? 0) * 24 + Number(h ?? 0);
  const minutes = Number(m ?? 0);
  const seconds = Number(s ?? 0);
  const paddedMinutes = hours > 0 ? String(minutes).padStart(2, '0') : String(minutes);
  return `${hours > 0 ? `${hours}:` : ''}${paddedMinutes}:${String(seconds).padStart(2, '0')}`;
}

export function VideoStatusCard({
  video,
  borderClassName,
  priorSubmissionCount,
  footer
}: {
  readonly video: VideoStatus;
  // The "already submitted" emphasis the page computes from the entity's prior submissions.
  readonly borderClassName?: string;
  readonly priorSubmissionCount?: number;
  // The entity's own actions (flag, classify, or the submitted state), rendered in the card footer.
  readonly footer?: ReactNode;
}) {
  const viewCount = formatCompactNumber(video.view_count);
  const likeCount = formatCompactNumber(video.like_count);
  const commentCount = formatCompactNumber(video.comment_count);
  const duration = formatIsoDuration(video.duration);
  const blockedCountries = video.region_restriction_blocked ?? [];
  const allowedCountries = video.region_restriction_allowed ?? [];
  const topicCategories = video.topic_categories ?? [];
  const tags = (video.tags ?? '')
    .split(',')
    .map((t) => t.trim())
    .filter(Boolean);
  const {thumbnailProps: blurThumbnailProps, controls: mediaControls} = useSensitiveMedia();
  const [lightboxOpen, setLightboxOpen] = useState(false);
  // Whether the analyst had unblurred or colourised the thumbnail when they opened it, so the
  // lightbox doesn't apply either again.
  const [lightboxRevealed, setLightboxRevealed] = useState(false);
  const [lightboxColourised, setLightboxColourised] = useState(false);
  const translatedTitle = useTranslatedText(video.id, video.title);
  const translatedDescription = useTranslatedText(video.id, video.description);
  const metaLine = [
    video.channel_title,
    video.published_at ? formatDate(video.published_at) : null,
    viewCount ? `${viewCount} views` : null,
    likeCount ? `${likeCount} likes` : null,
    commentCount ? `${commentCount} comments` : null
  ].filter(Boolean);

  return (
    <Card className={cn('border', borderClassName ?? 'border-border')}>
      <CardContent className="flex flex-col gap-4">
        <div className="flex gap-4">
          {/* The pill sits beside the lightbox button, not inside it: a button can't nest another. */}
          <div className="group bg-card relative aspect-video w-50 shrink-0 overflow-hidden">
            <button
              type="button"
              disabled={!video.thumbnail_url}
              aria-label={video.thumbnail_url ? 'View thumbnail' : undefined}
              className="relative size-full disabled:cursor-default"
              onClick={() => {
                setLightboxRevealed(!mediaControls.blur.applied);
                setLightboxColourised(!mediaControls.greyscale.applied);
                setLightboxOpen(true);
              }}>
              {video.thumbnail_url ? (
                <Thumbnail
                  src={video.thumbnail_url}
                  alt={video.title ?? video.id}
                  className={cn('size-full object-cover', BLUR_IMAGE_CLASS)}
                  {...blurThumbnailProps}
                />
              ) : (
                <span className="text-muted-foreground flex size-full items-center justify-center">
                  <Film className="size-8" />
                </span>
              )}
              {video.thumbnail_url ? (
                <span className="pointer-events-none absolute inset-0 flex items-end justify-end bg-black/0 p-2 transition-colors group-hover:bg-black/40">
                  <Maximize2 className="size-5 text-white opacity-0 transition-opacity group-hover:opacity-100" />
                </span>
              ) : null}
            </button>
            {video.thumbnail_url ? (
              <SensitiveMediaControls
                {...mediaControls}
                className={SENSITIVE_CONTROLS_VISIBILITY}
              />
            ) : null}
          </div>
          <ImageLightbox
            open={lightboxOpen}
            revealed={lightboxRevealed}
            colourised={lightboxColourised}
            // The lightbox exists to inspect the thumbnail closely, so it takes the largest size the
            // platform has rather than upscaling the card's own.
            imageUrl={video.thumbnail_url_high ?? video.thumbnail_url}
            alt={video.title ?? video.id}
            onClose={() => {
              setLightboxOpen(false);
            }}
          />
          <div className="flex min-w-0 flex-1 flex-col gap-1">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-foreground truncate text-sm font-medium">
                  {translatedTitle ?? 'Untitled video'}
                </p>
                {metaLine.length > 0 ? (
                  <p className="text-muted-foreground truncate text-xs">{metaLine.join(' · ')}</p>
                ) : null}
              </div>
              <div className="flex shrink-0 items-center gap-1">
                <Badge variant="outline">
                  <Film />
                  Video
                </Badge>
                {video.is_channel_monetization_enabled ? (
                  <Badge variant="outline">
                    <DollarSign />
                    Monetised
                  </Badge>
                ) : null}
              </div>
            </div>
            <TranslateToggle itemKey={video.id} texts={[video.title, video.description]} />
            {translatedDescription ? (
              <DescriptionDisclosure description={translatedDescription} />
            ) : null}
          </div>
        </div>

        <MetaGrid className="border-border border-t pt-4">
          <DetailField label="External ID">
            <span className="font-mono">{video.id}</span>
          </DetailField>
          <DetailField label="Platform status">
            <PlatformStatus status={video.privacy_status} />
          </DetailField>
          <DetailField label="Duration">{duration ?? '—'}</DetailField>
          <DetailField label="Channel">{video.channel_title ?? '—'}</DetailField>
          <DetailField label="Channel ID">
            <span className="font-mono">{video.channel_id ?? '—'}</span>
          </DetailField>
          <DetailField label="Published">{formatDate(video.published_at)}</DetailField>
          <DetailField label="Made for Kids">
            <BoolValue value={video.made_for_kids} />
          </DetailField>
          {/* Undocumented upstream (contentDetails.inBucket) and only returned to a key with special
              access, so an absent value renders as "Unknown" rather than "No". */}
          <DetailField label="Safe for Minors">
            <BoolValue value={video.safe_for_minors} />
          </DetailField>
          {/* Both bear directly on whether the content is submittable, so they sit in the summary
              rather than behind the expander. */}
          <DetailField label="Age Restricted">
            <BoolValue value={video.age_restricted} />
          </DetailField>
          <DetailField label="Livestream">
            <BoolValue value={video.is_livestream} />
          </DetailField>
          {priorSubmissionCount === undefined ? null : (
            <DetailField label="Prior submissions">
              <PriorSubmissions count={priorSubmissionCount} />
            </DetailField>
          )}
          {blockedCountries.length > 0 ? (
            <DetailField label="Region Restricted" className="col-span-full">
              <span className="text-destructive-ink flex items-center gap-1.5">
                <Globe className="size-3.5" />
                Blocked in {blockedCountries.join(', ')}
              </span>
            </DetailField>
          ) : null}
        </MetaGrid>

        <MoreDetails className="border-border border-t pt-3">
          <MetaGrid className="border-border rounded-lg border p-3">
            <DetailField label="Category">{video.category_name ?? '—'}</DetailField>
            <DetailField label="Embeddable">
              <BoolValue value={video.embeddable} />
            </DetailField>
            <DetailField label="Captions">
              <BoolValue value={video.has_captions} />
            </DetailField>
            <DetailField label="Quality">{video.definition?.toUpperCase() ?? '—'}</DetailField>
            <DetailField label="Licence">
              {video.license === 'creativeCommon' ? 'Creative Commons' : (video.license ?? '—')}
            </DetailField>
            <DetailField label="Default Language">{video.default_language ?? '—'}</DetailField>
            <DetailField label="Default Audio Language">
              {video.default_audio_language ?? '—'}
            </DetailField>
            <DetailField label="Channel Handle">{video.channel_custom_url ?? '—'}</DetailField>
            <DetailField label="Channel Country">{video.channel_country ?? '—'}</DetailField>
            <DetailField label="Channel Subscribers">
              {formatCompactNumber(video.channel_subscriber_count) ?? '—'}
            </DetailField>
            <DetailField label="Channel Videos">
              {formatCompactNumber(video.channel_video_count) ?? '—'}
            </DetailField>
            {/* Undocumented upstream, so shown only when the platform actually said so - an absent
                  value means "unknown", which is not the same as "not a short". */}
            {video.is_short === true ? <DetailField label="Short">Yes</DetailField> : null}
            {video.live_actual_start_time ? (
              <DetailField label="Streamed">
                {formatDate(video.live_actual_start_time)}
                {video.live_actual_end_time ? ` — ${formatDate(video.live_actual_end_time)}` : ''}
              </DetailField>
            ) : null}
            {allowedCountries.length > 0 ? (
              <DetailField label="Allowed Regions" className="col-span-full">
                {allowedCountries.join(', ')}
              </DetailField>
            ) : null}
            {topicCategories.length > 0 ? (
              <DetailField label="Topics" className="col-span-full">
                <TagList items={topicCategories} />
              </DetailField>
            ) : null}
            {tags.length > 0 ? (
              <DetailField label="Tags" className="col-span-full">
                <TagList items={tags} />
              </DetailField>
            ) : null}
          </MetaGrid>
        </MoreDetails>
      </CardContent>
      {footer ? <CardFooter className="justify-end gap-2">{footer}</CardFooter> : null}
    </Card>
  );
}
